import React from "react";
// Login page

// Default
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// Mui
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

// Images
import Logo from "../../assets/images/Mask group.png";

// Custom
import CustomInput from "../../forms/input/Input";
import ContainedButton from "../../forms/button/ContainedButton";
import { fetchPOSTRequest } from "../../utils/services";
import CustomToast from "../../components/toast/CustomToast";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setEnvironment } from "../../redux/features/AuthSlice";
import CustomInputLabelSelect from "../../forms/select/InputLabelCustomSelect";

// Types
interface FormData {
  email: string;
  password: string;
}

interface ToastState {
  message: string;
  severity: "error" | "success" | "warning" | "info" | "";
}

interface LoginResponse {
  status?: number;
  token?: string;
  refreshToken?: string;
}

const TOKENS = {
  token:
    "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJkZW1vQHZvbHRyZXVtLmNvbSIsInVzZXJJZCI6IjY5ZGY3NzEwLWU3NzQtMTFlZi1hNmExLWM1NjAzY2RhY2JjZCIsInNjb3BlcyI6WyJURU5BTlRfQURNSU4iXSwic2Vzc2lvbklkIjoiYWJjNmQ1MTYtMjBmYi00NGMzLTljMjctNWEzYmRjNDZjZTZlIiwiZXhwIjoxNzQ1MjQ3MjYxLCJpc3MiOiJ0aGluZ3Nib2FyZC5pbyIsImlhdCI6MTc0NTIzODI2MSwiZmlyc3ROYW1lIjoiRGVtbyIsImxhc3ROYW1lIjoiQWRtaW4iLCJlbmFibGVkIjp0cnVlLCJpc1B1YmxpYyI6ZmFsc2UsInRlbmFudElkIjoiYmZkYzg0MTAtZTc3My0xMWVmLWE2YTEtYzU2MDNjZGFjYmNkIiwiY3VzdG9tZXJJZCI6IjEzODE0MDAwLTFkZDItMTFiMi04MDgwLTgwODA4MDgwODA4MCJ9.y-Cv6tJV5KrkZidzdUKh-0nau5pn8eaydZ-7KWHtt6U5oC3X1t9CGMu7SoLvXMgpeqQRv3rIlm-0gmGnWVzV9w",
  refreshToken:
    "eyJhbGciOiJIUzUxMiJ9.eyJzdWIiOiJkZW1vQHZvbHRyZXVtLmNvbSIsInVzZXJJZCI6IjY5ZGY3NzEwLWU3NzQtMTFlZi1hNmExLWM1NjAzY2RhY2JjZCIsInNjb3BlcyI6WyJSRUZSRVNIX1RPS0VOIl0sInNlc3Npb25JZCI6ImFiYzZkNTE2LTIwZmItNDRjMy05YzI3LTVhM2JkYzQ2Y2U2ZSIsImV4cCI6MTc0NTg0MzA2MSwiaXNzIjoidGhpbmdzYm9hcmQuaW8iLCJpYXQiOjE3NDUyMzgyNjEsImlzUHVibGljIjpmYWxzZSwianRpIjoiMzU5ZTY4NzktYmEzYS00ZTg2LThjNjEtOGFmZWM4YmU2YWJiIn0.Bj9VVd4jTGvHD0tWwVCL18_y42JjR1wAjm8P8CpqV8qyoadrg0Y84yQssRI0W3HsNF8uCH1jFwGmU0U80jaNEg",
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const admin = { email: "demo@voltreum.com", password: "forDemoOnly" };
  const dispatch = useAppDispatch();
  const { environment } = useAppSelector((state) => state.auth);

  const ENV_OPTIONS = [
    { type: "Demo", value: "Demo" },
    { type: "Test", value: "Test" },
  ];

  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [loading, setLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (): Promise<void> => {
    const userBody = {
      username: formData.email,
      password: formData.password,
    };
    setLoading(true);
    try {
      const response: LoginResponse = await fetchPOSTRequest(
        `auth/login`,
        userBody,
        false,
      );

      if (response?.status === 401) {
        setToast({
          message: "Invalid credentials",
          severity: "error",
        });
      } else {
        // dispatch(setAccessToken(response?.token));
        setToast({
          message: "Logged in successfully",
          severity: "success",
        });
        console.log("---", response?.token);
        localStorage.setItem("authToken", response?.token || ""); // token
        localStorage.setItem("refreshToken", response?.refreshToken || ""); // refreshToken
        setTimeout(() => {
          navigate("/dashboard-360");
        }, 1000);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred";
      console.error("Error during login request:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Card className="login_card">
          <img src={Logo} alt="Logo" />
          <Typography className="login_card_title"> Login </Typography>
          <Stack direction={"column"} gap={"20px"}>
            <CustomInput
              label={"Email"}
              name={"email"}
              value={formData.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange(e)
              }
            />
            <CustomInput
              label={"Password"}
              type={"password"}
              name={"password"}
              value={formData.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange(e)
              }
            />
            <CustomInputLabelSelect
              title={"Mode "}
              value={
                ENV_OPTIONS.find((opt) => opt.value === environment) ||
                ENV_OPTIONS[0]
              }
              options={ENV_OPTIONS}
              onChange={(opt: any) =>
                dispatch(setEnvironment(opt.value as "Demo" | "Test"))
              }
            />
          </Stack>
          <ContainedButton
            label={"Login"}
            loading={loading}
            sx={{
              width: "100%",
              mt: "20px",
              fontWeight: 600,
              color: "#ffffff",
              padding: "12px 15px",
            }}
            onClick={handleSubmit}
          />
        </Card>
        <CustomToast
          open={toast?.message}
          severity={toast?.severity}
          setToast={setToast}
        />
      </Box>
    </Container>
  );
};

export default Login;
